/**
 * FreshnessGauge — a shared HUD widget for the precommit / expiry arcade games.
 *
 * It visualizes a closing validity window: a radial arc that drains as the
 * witness ages, plus a current-vs-lapsed verdict. It is presentation only — it
 * never decides whether a witness is current; the pure model owns that verdict
 * and passes `fraction` (1 = freshly sealed, 0 = lapsed) and `lapsed` in.
 *
 * The structural lesson it serves: a lapsed witness cannot authorize, and there
 * is no move to back-date one. The gauge makes the closing window legible so the
 * player re-seals before it lapses.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

export interface FreshnessGaugeProps {
  /** Remaining validity in [0, 1]: 1 just sealed, 0 lapsed. */
  readonly fraction: number;
  /** Whether the window has lapsed (the witness is no longer current). */
  readonly lapsed: boolean;
  /** Color for a current (fresh) window. */
  readonly fresh: string;
  /** Color for a low / lapsed window. */
  readonly danger: string;
  /** Track color behind the arc. */
  readonly track: string;
  /** Honor prefers-reduced-motion: drop the sweep transition when true. */
  readonly reducedMotion: boolean;
  /** Diameter in pixels. Defaults to 84. */
  readonly size?: number;
  /** Optional caption under the verdict (e.g. the witness name). */
  readonly label?: string;
}

const LOW_THRESHOLD = 0.25;

/** A radial freshness arc with a CURRENT / LAPSED verdict. */
export function FreshnessGauge({
  fraction,
  lapsed,
  fresh,
  danger,
  track,
  reducedMotion,
  size = 84,
  label,
}: FreshnessGaugeProps): React.JSX.Element {
  const clamped = Math.max(0, Math.min(1, fraction));
  const low = clamped <= LOW_THRESHOLD;
  const color = lapsed || low ? danger : fresh;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * clamped;
  const verdict = lapsed ? "LAPSED" : "CURRENT";
  return (
    <div
      role="img"
      aria-label={`Witness window ${verdict.toLowerCase()}, ${(clamped * 100).toFixed(0)} percent remaining`}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={track}
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${String(dash)} ${String(circumference)}`}
            style={{
              transition: reducedMotion
                ? "none"
                : "stroke-dasharray 120ms linear, stroke 200ms ease",
            }}
          />
        </svg>
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: "0.04em",
            color,
          }}
        >
          {verdict}
        </span>
      </div>
      {label !== undefined && (
        <span
          style={{ fontSize: 11, fontWeight: 600, color, textAlign: "center" }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
